import { Bar, BarChart, CartesianGrid, Cell, Label, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'chart';
import { useMemo } from 'react';
import { Box, Stack, type Theme, Typography, useBreakpoint, useTheme } from 'ui';

const TOTAL_BUCKETS = 11;
const MIN_PERCENTAGE = 70;
const MAX_PERCENTAGE = 100;

interface HistogramBucket {
  label: string;
  count: number;
  percentage: number;
  isTarget: boolean;
}

const roundPercentage = (value: number): number => {
  return Math.round(value * 100) / 100;
};

// Cache for bucket ranges to avoid recalculating for the same target grade
const bucketRangesCache = new Map<number, number[]>();

/**
 * Checks if a cached bucket range exists for the given target grade
 */
const getCachedBucketRanges = (targetGrade: number): number[] | undefined => {
  const roundedGrade = Math.round(targetGrade);

  if (!bucketRangesCache.has(roundedGrade)) {
    return;
  }

  const cachedRanges = bucketRangesCache.get(roundedGrade);

  if (!cachedRanges) {
    return;
  }

  return [...cachedRanges]; // Return a copy to prevent mutation
};

/**
 * Calculates the min and max bucket values based on target grade
 */
const calculateBucketRange = (targetGrade: number): { minBucket: number; maxBucket: number } => {
  const validTargetGrade = Math.max(MIN_PERCENTAGE, Math.min(MAX_PERCENTAGE, Math.round(targetGrade)));
  const idealBucketsOnEachSide = Math.floor(TOTAL_BUCKETS / 2);

  // Calculate the range limits based on the target grade
  let minBucket = validTargetGrade - idealBucketsOnEachSide;
  let maxBucket = validTargetGrade + idealBucketsOnEachSide;

  // Adjust if we go below MIN_PERCENTAGE
  if (minBucket < MIN_PERCENTAGE) {
    const adjustment = MIN_PERCENTAGE - minBucket;
    minBucket = MIN_PERCENTAGE;
    maxBucket = Math.min(MAX_PERCENTAGE, maxBucket + adjustment);
  }

  // Adjust if we go above MAX_PERCENTAGE
  if (maxBucket > MAX_PERCENTAGE) {
    const adjustment = maxBucket - MAX_PERCENTAGE;
    maxBucket = MAX_PERCENTAGE;
    minBucket = Math.max(MIN_PERCENTAGE, minBucket - adjustment);
  }

  return { maxBucket, minBucket };
};

/**
 * Fills bucket ranges when we need special buckets (less than TOTAL_BUCKETS regular buckets)
 */
const fillWithSpecialBuckets = (
  bucketRanges: number[],
  minBucket: number,
  maxBucket: number,
  actualBuckets: number,
  isTargetCloserToMin: boolean
): void => {
  if (isTargetCloserToMin) {
    // Add "<MIN_PERCENTAGE" bucket at the beginning
    let index = 0;
    bucketRanges[index++] = -1;

    // Fill the regular range
    for (let i = minBucket; i <= maxBucket && index < TOTAL_BUCKETS; i++) {
      bucketRanges[index++] = i;
    }

    // Add ">MAX_PERCENTAGE" bucket at the end if needed
    if (index < TOTAL_BUCKETS) {
      bucketRanges[index] = -2;
    }
  } else {
    // Calculate how many special buckets we need
    const specialBucketsNeeded = TOTAL_BUCKETS - actualBuckets;
    let specialBucketsStart = 0;
    let specialBucketsEnd = 0;

    // Distribute special buckets evenly
    if (specialBucketsNeeded % 2 === 0) {
      specialBucketsStart = specialBucketsEnd = specialBucketsNeeded / 2;
    } else {
      specialBucketsEnd = Math.ceil(specialBucketsNeeded / 2);
      specialBucketsStart = specialBucketsNeeded - specialBucketsEnd;
    }

    // Add special buckets at the beginning
    for (let i = 0; i < specialBucketsStart; i++) {
      bucketRanges[i] = -1;
    }

    // Add regular buckets
    for (let i = 0; i < actualBuckets; i++) {
      bucketRanges[i + specialBucketsStart] = minBucket + i;
    }

    // Add special buckets at the end
    for (let i = 0; i < specialBucketsEnd; i++) {
      bucketRanges[specialBucketsStart + actualBuckets + i] = -2;
    }
  }
};

/**
 * Fills bucket ranges when we have enough or too many regular buckets
 */
const fillWithRegularBuckets = (
  bucketRanges: number[],
  minBucket: number,
  actualBuckets: number,
  isTargetCloserToMin: boolean
): void => {
  if (bucketRanges.length === actualBuckets) {
    // Exact fit
    for (let i = 0; i < actualBuckets; i++) {
      bucketRanges[i] = minBucket + i;
    }
  } else {
    // Need to trim excess buckets
    const excess = actualBuckets - TOTAL_BUCKETS;
    const startOffset = isTargetCloserToMin ? 0 : excess;

    for (let i = 0; i < TOTAL_BUCKETS; i++) {
      bucketRanges[i] = minBucket + startOffset + i;
    }
  }
};

/**
 * Generates bucket ranges based on target grade
 * Creates exactly 11 buckets with the target grade as close to the center as possible
 */
const generateBucketRanges = (targetGrade: number): number[] => {
  // Check cache first
  const cachedRanges = getCachedBucketRanges(targetGrade);
  if (cachedRanges) {
    return cachedRanges;
  }

  const roundedGrade = Math.round(targetGrade);
  const { minBucket, maxBucket } = calculateBucketRange(roundedGrade);

  // Pre-allocate array with exact size
  const bucketRanges = new Array<number>(TOTAL_BUCKETS);
  const actualBuckets = maxBucket - minBucket + 1;
  const isTargetCloserToMin = roundedGrade - MIN_PERCENTAGE <= MAX_PERCENTAGE - roundedGrade;

  // Fill bucket ranges based on the number of actual buckets
  if (actualBuckets < TOTAL_BUCKETS) {
    fillWithSpecialBuckets(bucketRanges, minBucket, maxBucket, actualBuckets, isTargetCloserToMin);
  } else {
    fillWithRegularBuckets(bucketRanges, minBucket, actualBuckets, isTargetCloserToMin);
  }

  // Store in cache
  bucketRangesCache.set(roundedGrade, [...bucketRanges]);

  return bucketRanges;
};

// Cache for bucket assignments to avoid recalculating
const bucketAssignmentCache = new Map<number, number>();

// Precomputed bucket labels
const bucketLabelCache = new Map<string, string>();

/**
 * Initializes bucket map with zero counts
 */
const initializeBucketMap = (
  bucketRanges: number[],
  roundedTargetGrade: number
): Map<number, { count: number; isTarget: boolean }> => {
  const bucketMap = new Map<number, { count: number; isTarget: boolean }>();

  for (let i = 0; i < bucketRanges.length; i++) {
    const percentage = bucketRanges[i];
    bucketMap.set(percentage, {
      count: 0,
      isTarget: percentage === roundedTargetGrade,
    });
  }

  return bucketMap;
};

/**
 * Groups weight values by percentage for batch processing
 */
const groupWeightsByPercentage = (targetBaleWeights: number[]): Map<number, number> => {
  const weightGroups = new Map<number, number>();

  for (const weight of targetBaleWeights) {
    const percentValue = Math.round(roundPercentage(weight) * 100);
    weightGroups.set(percentValue, (weightGroups.get(percentValue) || 0) + 1);
  }

  return weightGroups;
};

/**
 * Finds the appropriate bucket for a percentage value
 */
const findBucketForPercentage = (percentValue: number, bucketRanges: number[]): number => {
  // Check if we've already calculated this bucket assignment
  if (bucketAssignmentCache.has(percentValue)) {
    const cachedBucket = bucketAssignmentCache.get(percentValue);
    if (cachedBucket !== undefined) {
      return cachedBucket;
    }
  }

  let assignedBucket: number;

  if (percentValue < MIN_PERCENTAGE) {
    // Values below minimum go to first bucket
    assignedBucket = bucketRanges[0];
  } else if (percentValue > MAX_PERCENTAGE) {
    // Values above maximum go to last bucket
    assignedBucket = bucketRanges[bucketRanges.length - 1];
  } else {
    // Find closest regular bucket
    let closestBucket = bucketRanges.find((b) => b >= 0) || bucketRanges[0];
    let minDistance = Math.abs(percentValue - closestBucket);

    for (const bucket of bucketRanges) {
      if (bucket < 0) continue; // Skip special buckets

      const distance = Math.abs(percentValue - bucket);
      if (distance < minDistance) {
        minDistance = distance;
        closestBucket = bucket;
      }
    }

    assignedBucket = closestBucket;
  }

  // Cache the bucket assignment
  bucketAssignmentCache.set(percentValue, assignedBucket);

  return assignedBucket;
};

/**
 * Counts weights in their appropriate buckets
 */
const countWeightsInBuckets = (
  weightGroups: Map<number, number>,
  bucketRanges: number[],
  bucketMap: Map<number, { count: number; isTarget: boolean }>
): void => {
  for (const [percentValue, count] of weightGroups.entries()) {
    const assignedBucket = findBucketForPercentage(percentValue, bucketRanges);
    const bucketData = bucketMap.get(assignedBucket);

    if (bucketData) {
      bucketData.count += count;
    }
  }
};

/**
 * Formats a bucket label based on its percentage and position
 */
const formatBucketLabel = (percentage: number, index: number, bucketRangesLength: number): string => {
  if (percentage === -1 || index === 0) {
    // First bucket gets < symbol
    return `<${percentage === -1 ? MIN_PERCENTAGE : percentage}`;
  }

  if (percentage === -2 || index === bucketRangesLength - 1) {
    const displayValue = percentage === -2 ? MAX_PERCENTAGE : percentage;

    // Edge case: if the furthest right label is exactly 100, don't show '100>'
    if (displayValue === MAX_PERCENTAGE) {
      return `${displayValue}`;
    }
    return `${displayValue}>`;
  }

  return `${percentage}`;
};

/**
 * Gets a formatted label for a bucket, using cache when possible
 */
const getBucketLabel = (percentage: number, index: number, bucketRangesLength: number): string => {
  // Create a unique key for label caching
  const labelKey = `${percentage}_${index}_${bucketRangesLength}_${percentage === MAX_PERCENTAGE}`;

  if (bucketLabelCache.has(labelKey)) {
    const cachedLabel = bucketLabelCache.get(labelKey);
    return cachedLabel !== undefined ? cachedLabel : '';
  }

  const label = formatBucketLabel(percentage, index, bucketRangesLength);

  // Cache the label
  bucketLabelCache.set(labelKey, label);

  return label;
};

/**
 * Creates the final histogram bucket objects
 */
const createHistogramBucketObjects = (
  bucketRanges: number[],
  bucketMap: Map<number, { count: number; isTarget: boolean }>
): HistogramBucket[] => {
  const result: HistogramBucket[] = [];

  for (let i = 0; i < bucketRanges.length; i++) {
    const percentage = bucketRanges[i];
    const bucketData = bucketMap.get(percentage);

    if (!bucketData) continue;

    const label = getBucketLabel(percentage, i, bucketRanges.length);

    result.push({
      count: bucketData.count,
      isTarget: bucketData.isTarget,
      label,
      percentage,
    });
  }

  return result;
};

/**
 * Creates histogram buckets from target bale weights and target grade
 */
const createHistogramBuckets = (targetBaleWeights: number[], targetGrade: number): HistogramBucket[] => {
  const bucketRanges = generateBucketRanges(targetGrade);
  const roundedTargetGrade = Math.round(targetGrade);

  // Initialize buckets with zero counts
  const bucketMap = initializeBucketMap(bucketRanges, roundedTargetGrade);

  // Group weights by percentage value for batch processing
  const weightGroups = groupWeightsByPercentage(targetBaleWeights);

  // Count weights in their appropriate buckets
  countWeightsInBuckets(weightGroups, bucketRanges, bucketMap);

  // Create the final histogram bucket objects
  return createHistogramBucketObjects(bucketRanges, bucketMap);
};

const formatTooltip = (value: number): string => {
  return `${value} bales`;
};

const formatTooltipLabel = (label: string): string => {
  return `Grade: ${label}`;
};

interface TargetBaleWeightsHistogramProps {
  targetBaleWeights: number[];
  targetGrade: number;
  title?: string;
}

interface CustomXAxisTickProps {
  x?: number;
  y?: number;
  payload?: {
    value: string;
  };
  theme: Theme;
  targetGrade: number;
  fontSize: number;
}

const CustomXAxisTick = (props: CustomXAxisTickProps) => {
  const { x = 0, y = 0, payload = { value: '' }, theme, targetGrade } = props;

  const isTarget = targetGrade === Number(payload.value);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        dy={16}
        textAnchor="middle"
        fill={isTarget ? theme.palette.chart.main : theme.palette.text.primary}
        fontSize={props.fontSize}
        fontWeight={isTarget ? 'bold' : 'normal'}
      >
        {payload.value}
      </text>
    </g>
  );
};

const CustomLegend = ({ theme, isMobile }: { theme: Theme; isMobile: boolean }) => {
  return (
    <Stack
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      gap={1}
      width="100%"
      justifyContent="center"
      alignItems="center"
      padding={1}
      paddingTop={0}
      marginBottom={isMobile ? 2 : 4}
    >
      <Stack display="flex" flexDirection="row" gap={1} alignItems="center">
        <Box
          width={10}
          height={10}
          fontSize={8}
          sx={{
            backgroundColor: theme.palette.chart.light,
          }}
        />
        Other detected material grades
      </Stack>
      <Stack display="flex" flexDirection="row" gap={1} alignItems="center">
        <Box
          width={10}
          height={10}
          fontSize={8}
          sx={{
            backgroundColor: theme.palette.chart.main,
          }}
        />
        Material grade
      </Stack>
    </Stack>
  );
};

export const TargetBaleWeightsHistogram = ({
  targetBaleWeights,
  targetGrade,
  title,
}: TargetBaleWeightsHistogramProps) => {
  const { isMobile } = useBreakpoint();
  const theme = useTheme();

  const histogramData = useMemo(() => {
    return createHistogramBuckets(targetBaleWeights, targetGrade);
  }, [targetBaleWeights, targetGrade]);

  const chartHeight = isMobile ? 250 : 300;
  const fontSize = isMobile ? 12 : 16;
  const barSize = isMobile ? 20 : 25;
  const yAxisWidth = isMobile ? 40 : 60;
  const xAxisPadding = isMobile ? { left: 15, right: 15 } : { left: 30, right: 30 };

  return (
    <Box data-testid="target-bale-weights-histogram" width="100%" padding={2}>
      {title ? (
        <Typography variant={isMobile ? 'subtitle1' : 'h6'} align="center" gutterBottom sx={{ mb: 2 }}>
          {title}
        </Typography>
      ) : null}

      <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={histogramData}
          barSize={barSize}
          barCategoryGap={0}
          layout="horizontal"
          barGap={0}
          margin={{ bottom: 30, left: 10, right: 10, top: 10 }}
        >
          <Legend verticalAlign="top" content={<CustomLegend theme={theme} isMobile={isMobile} />} />
          <CartesianGrid vertical={false} strokeDasharray="3 3" />
          <XAxis
            dataKey="label"
            scale="point"
            padding={xAxisPadding}
            tick={<CustomXAxisTick theme={theme} targetGrade={targetGrade} fontSize={fontSize} />}
            tickSize={5}
            fontSize={fontSize}
            interval={0}
          >
            <Label value="Predicted UBC content %" position="bottom" offset={15} />
          </XAxis>
          <YAxis allowDecimals={false} tickMargin={isMobile ? 5 : 15} width={yAxisWidth} fontSize={fontSize}>
            <Label value="Number of bales" position="left" angle={-90} dy={-50} />
          </YAxis>
          <Tooltip
            formatter={formatTooltip}
            labelFormatter={(label: string) => formatTooltipLabel(label)}
            cursor={{ fill: theme.palette.action.hover }}
          />
          <Bar dataKey="count" name="Bales" radius={[4, 4, 0, 0]} barSize={barSize}>
            {histogramData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.isTarget ? theme.palette.chart.main : theme.palette.chart.light}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};
